// Updated per olives brief: types+brands, hero-only, dropdown minimal, Products page full details.
'use client';

import React from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cn } from '@/lib/utils';

type SetActiveFn = React.Dispatch<React.SetStateAction<string | null>>;

export const MenuItem = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  {
    setActive: SetActiveFn;
    active: string | null;
    item: string;
    children?: React.ReactNode;
    id?: string;
    href?: LinkProps['to'];
  }
>(({ setActive, active, item, children, id, href }, ref) => {
  const isOpen = active === item;
  const triggerClassName = cn(
    'inline-flex rounded-full px-3 py-1 text-slate-700 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
    isOpen && 'text-primary',
  );

  const renderTrigger = () => {
    if (href) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          id={id}
          to={href}
          aria-expanded={children ? isOpen : undefined}
          className={triggerClassName}
          onClick={() => setActive(null)}
        >
          {item}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        id={id}
        type="button"
        aria-expanded={isOpen}
        onClick={() => setActive(isOpen ? null : item)}
        className={cn(triggerClassName, 'text-slate-900')}
      >
        {item}
      </button>
    );
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setActive(item)}
      onMouseLeave={() => setActive(null)}
      onFocusCapture={() => setActive(item)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setActive(null);
        }
      }}
    >
      {renderTrigger()}
      {children ? (
        <div
          className={cn(
            'absolute left-1/2 top-full -translate-x-1/2 pt-4 transition-all duration-200 ease-out',
            isOpen ? 'pointer-events-auto opacity-100 translate-y-0' : 'pointer-events-none opacity-0 -translate-y-2',
          )}
        >
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl backdrop-blur-sm">
            <div aria-labelledby={id} className="h-full w-max p-4">
              {children}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});

MenuItem.displayName = 'MenuItem';

export const Menu = ({
  setActive,
  children,
  className,
}: {
  setActive: SetActiveFn;
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <nav
      onMouseLeave={() => setActive(null)}
      className={cn(
        'relative flex items-center space-x-5 rounded-full bg-white/95 px-6 py-3 shadow-input shadow-md',
        className,
      )}
    >
      {children}
    </nav>
  );
};

type HoveredLinkProps = LinkProps & {
  className?: string;
};

export const HoveredLink = ({ children, to, className, ...props }: HoveredLinkProps) => {
  return (
    <Link
      to={to}
      className={cn(
        'rounded-full px-3 py-1 text-slate-700 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        className,
      )}
      {...props}
    >
      {children}
    </Link>
  );
};
